<template>
  <div ref="root">
    <UiTooltip theme="material" tooltip-position="top" :label="hint">
      <div
        @click="showDropdown"
        class="tw:relative tw:cursor-pointer tw:border tw:rounded-lg tw:flex box-size-44 tw:items-center tw:px-3 tw:py-1 tw:pr-8 tw:h-[41px]"
        :class="getFieldClass(show, disabled)"
      >
        <span class="tw:truncate tw:absolute tw:top-[3px] tw:text-xs tw:font-semibold">{{ $t(label) }}</span>
        <span class="tw:truncate tw:absolute tw:bottom-[3px] tw:text-sm tw:max-w-[220px]">
          {{ $t(value.label, value.params || {}) }}</span
        >

        <ChevronRight class="tw:absolute tw:size-6" :class="show ? 'arrow-dropdown-open' : 'arrow-dropdown'" />
      </div>
    </UiTooltip>
    <div class="tw:relative tw:top-[5px]" v-if="show && !disabled">
      <div
        class="tw:absolute tw:min-h-[50px] tw:min-w-full tw:w-auto tw:rounded tw:bg-white dropdown-card tw:z-20 tw:max-h-[330px] tw:overflow-y-auto"
        :class="'tw:left-0'"
      >
        <!-- Grouped Options -->
        <template v-if="isGrouped">
          <div v-for="(group, groupIndex) in options" :key="groupIndex">
            <!-- Group Header -->
            <div class="tw:px-4 tw:py-2 tw:text-sm tw:font-bold tw:bg-gray-200">
              {{ $t(group.group) }}
            </div>
            <!-- Group Options -->
            <ul>
              <li
                v-for="option in group.options"
                :key="option.value"
                @click="onSelect({ option, name })"
                class="tw:cursor-pointer tw:hover:bg-blue-100 tw:hover:text-blue-700 tw:p-4 nowrap tw:text-sm"
                :class="getOptionClass(option.value === value.value)"
              >
                {{ $t(option.label, option.params || {}) }}
              </li>
            </ul>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onDeactivated } from 'vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'

const props = defineProps({
  options: Array,
  name: String,
  value: Object,
  disabled: Boolean,
  hint: String,
  label: String,
  withTranslations: {
    type: Boolean,
    default: false,
  },
})

const root = ref(null)
const show = ref(false)

const isGrouped = computed(() => {
  return props.options?.length > 0 && props.options[0]?.group !== undefined
})

const showDropdown = () => {
  if (!props.disabled) {
    show.value = !show.value
  }
}

const emit = defineEmits(['select'])
const onSelect = ({ option, name }) => {
  emit('select', { option, name })
  showDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onDeactivated(() => {
  document.removeEventListener('click', handleClickOutside)
})

const handleClickOutside = evt => {
  if (root?.value !== null && !root?.value?.contains(evt.target)) {
    show.value = false
  }
}

const getFieldClass = (show, disabled) => {
  const classes = []
  if (disabled) {
    classes.push('tw:border-gray-300 tw:text-gray-300')
  }
  if (show) {
    classes.push('tw:border-blue-700')
  }
  if (!show && !disabled) {
    classes.push('tw:border-gray-500')
  }
  return classes.join(' ')
}

const getOptionClass = (selected, position = null) => {
  const classes = []

  // Only apply rounded corners for flat list
  if (position !== null) {
    if (position === 0) {
      classes.push('tw:rounded-t')
    } else if (props.options.length - 1 === position) {
      classes.push('tw:rounded-b')
    }
  }

  if (selected) {
    classes.push('tw:text-blue-700 tw:bg-blue-100')
  }

  return classes.join(' ')
}
</script>

<style scoped>
.arrow-dropdown {
  top: 50%;
  transform: translateY(-45%) rotate(90deg);
  right: 6px;
  pointer-events: none;
}
.arrow-dropdown-open {
  top: 50%;
  transform: translateY(-45%) rotate(270deg);
  right: 6px;
  pointer-events: none;
}
.dropdown-card {
  box-shadow:
    0 2px 4px 0 rgba(63, 87, 189, 0.1),
    0 2px 10px 0 rgba(108, 112, 122, 0.15);
}
.nowrap {
  text-wrap: nowrap;
}
</style>
